/**
 * The command registry — the backing store for the ⌘K command palette.
 *
 * Features register their actions into it (`registerCommands`) and unregister
 * on unmount; the palette renders whatever is currently registered. Keeping the
 * store framework-agnostic (a plain module singleton) lets any layer open the
 * palette or run a command without prop-drilling. React reads it through
 * `useSyncExternalStore` (see `hooks/useCommands.ts`).
 */
export interface Command {
  /** Stable unique id; re-registering the same id replaces it. */
  id: string;
  /** Visible, already-translated label. */
  title: string;
  /** Already-translated group heading the command is listed under. */
  group: string;
  /** Extra terms to match against, beyond the title. */
  keywords?: string[];
  /** Optional global shortcut, e.g. `"mod+k"` — display + matching. */
  shortcut?: string;
  /** Optional leader-key chord, e.g. `"g i"` (press g, then i) — for bindings
   *  that would collide with browser/OS combos like ⌘⇧I. */
  chord?: string;
  /** Fire even while the user is typing in an input/textarea. Off by default. */
  runInInput?: boolean;
  perform: () => void;
}

type Listener = () => void;

const registry = new Map<string, Command>();
const listeners = new Set<Listener>();

let snapshot: Command[] = [];
let open = false;

function recompute(): void {
  snapshot = [...registry.values()];
}

function emit(): void {
  for (const listener of listeners) listener();
}

/** Register commands; returns an unregister fn (call it on unmount). */
export function registerCommands(commands: Command[]): () => void {
  for (const command of commands) registry.set(command.id, command);
  recompute();
  emit();
  return () => {
    for (const command of commands) registry.delete(command.id);
    recompute();
    emit();
  };
}

/** Stable snapshot of the registered commands (safe for useSyncExternalStore). */
export function getCommands(): Command[] {
  return snapshot;
}

export function isPaletteOpen(): boolean {
  return open;
}

export function setPaletteOpen(next: boolean): void {
  if (open === next) return;
  open = next;
  emit();
}

export function subscribeCommands(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Filter commands by a free-text query against title + group + keywords
 * (case-insensitive substring). A blank query returns everything. Pure — the
 * palette's search box and its unit tests both use this.
 */
export function filterCommands(commands: Command[], query: string): Command[] {
  const q = query.trim().toLowerCase();
  if (!q) return commands;
  return commands.filter((command) => {
    const haystack = [command.title, command.group, ...(command.keywords ?? [])]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}
